import React from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Icon, Switch } from '@sg/garnish';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { iconPalettes, ScanToPayPalette, textPalettes } from '../../utils';

export const ScanToPayCreditBalance = (props: ScanToPayCreditBalanceProps) => {
  const {
    useCredit,
    shouldAllowToggle,
    creditBalance,
    palette,
    onToggleUseCredit,
  } = props;

  const { formatNumber, formatMessage } = useIntl();
  const a11yLabel = formatMessage(messages.a11yLabel);

  const creditBalanceFormatted = formatNumber(Math.round(creditBalance / 100), {
    style: 'currency',
    currency: 'USD',
  });

  return (
    <View style={styles.container}>
      <View style={styles.leftSide}>
        <Icon
          name="IconCredit"
          width={32}
          height={32}
          color={iconPalettes[palette]}
        />

        <View>
          <BodyText sizeMatch={['14']} style={textPalettes[palette]}>
            <FormattedMessage {...messages.title} />
          </BodyText>

          <BodyText bold sizeMatch={['16']} style={textPalettes[palette]}>
            {creditBalanceFormatted}
          </BodyText>
        </View>
      </View>

      {shouldAllowToggle ? (
        <Switch
          showIcons={false}
          palette="success"
          size="medium"
          accessibilityLabel={a11yLabel}
          value={useCredit}
          onChange={onToggleUseCredit}
        />
      ) : null}
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'SG Credit Balance',
    description: 'Scan To Pay | Credit Balance | Title',
  },
  a11yLabel: {
    defaultMessage: 'Toggle use credit',
    description: 'Scan To Pay | Credit Balance | Accessibility Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
  leftSide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayCreditBalanceProps = {
  useCredit: boolean;
  shouldAllowToggle: boolean;
  creditBalance: number;
  palette: ScanToPayPalette;
  onToggleUseCredit: (useCredit: boolean) => void;
};
